interface JobTitleSource {
  readonly url: string;
  readonly organizationName?: string;
  readonly projectName?: string;
}

interface JobTitle {
  /**
   * Primary identifier shown as the row's headline. `Org / Project` when both
   * are available; falls back to whichever is set; finally falls back to the
   * URL so empty rows are never blank.
   */
  readonly primary: string;
  /**
   * Secondary supporting line. Set to the URL when `primary` is *not* already
   * the URL — keeps the URL visible (testers still want it as quick context)
   * without duplicating it twice on the same row. `null` means "the primary
   * line is the URL, don't render this slot at all".
   */
  readonly secondary: string | null;
}

/**
 * Compute the row title shape for a queue job. #3924 made
 * `Organization / Project` the primary identifier so testers see *what they're
 * about to test* (the customer + product) before they see the URL string.
 *
 * Pure derivation — exported separately so JobRow stays presentational and so
 * follow-up surfaces (mobile app, web JobCard) can adopt the same shape with
 * one import instead of re-deriving the priority order.
 */
export function getJobTitle(job: JobTitleSource): JobTitle {
  const { url, organizationName, projectName } = job;
  const orgProject = formatOrgProject(organizationName, projectName);
  if (!orgProject) {
    return { primary: url, secondary: null };
  }
  return { primary: orgProject, secondary: url };
}

function formatOrgProject(
  organizationName: string | undefined,
  projectName: string | undefined,
): string | null {
  if (organizationName && projectName) return `${organizationName} / ${projectName}`;
  if (organizationName) return organizationName;
  if (projectName) return projectName;
  return null;
}
